import { useState, useRef } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';
import { gradientButton } from '../theme';

interface ImageCropperProps {
  imageUrl: string;
  onCropComplete: (croppedImageUrl: string) => void;
  onCancel: () => void;
  aspectRatio?: number;
}

function ImageCropper({ imageUrl, onCropComplete, onCancel, aspectRatio = 1 }: ImageCropperProps) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Fonction pour créer un crop carré centré
  function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
  ) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    )
  }

  // Initialiser le crop carré quand l'image est chargée
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerAspectCrop(width, height, aspectRatio);
    setCrop(crop);
    
    // Convertir le crop en pixels pour completedCrop
    if (imgRef.current) {
      const pixelCrop: PixelCrop = {
        unit: 'px',
        x: Math.round((crop.x * width) / 100),
        y: Math.round((crop.y * height) / 100),
        width: Math.round((crop.width * width) / 100),
        height: Math.round((crop.height * height) / 100)
      };
      setCompletedCrop(pixelCrop);
    }
  };

  const getCroppedImg = async (image: HTMLImageElement, pixelCrop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        1
      );
    });
  };

  const compressImage = async (blob: Blob): Promise<Blob> => {
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 800,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.9,
    };

    try {
      return await imageCompression(
        new File([blob], 'cropped.jpg', { type: 'image/jpeg' }), 
        options
      );
    } catch (error) {
      console.error('Error compressing image:', error);
      return blob;
    }
  };

  const handleValidate = async () => {
    if (!imgRef.current || !completedCrop) return;

    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      const compressedBlob = await compressImage(croppedBlob);
      
      // Créer un reader pour convertir le blob en base64
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          onCropComplete(reader.result);
        }
      };
      reader.readAsDataURL(compressedBlob);
    } catch (error) {
      console.error('Error processing image:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-[#0A0A0A] rounded-2xl overflow-hidden">
        <div className="p-4">
          <h3 className="text-xl font-semibold text-white mb-4">{t('imageCropper.title')}</h3>
          <p className="text-gray-400 text-sm mb-4">{t('imageCropper.subtitle')}</p>
          <div className="relative">
            <ReactCrop
              crop={crop}
              onChange={c => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
              circularCrop={false}
              keepSelection={true}
              minWidth={100}
              minHeight={100}
              className="max-h-[70vh] rounded-xl overflow-hidden"
            >
              <img
                ref={imgRef}
                src={imageUrl}
                alt={t('imageCropper.preview')}
                className="max-h-[70vh] w-full object-contain"
                crossOrigin="anonymous"
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              {t('imageCropper.cancel')}
            </button>
            <button
              onClick={handleValidate}
              className={`px-6 py-2 rounded-lg ${gradientButton} text-white`}
            >
              {t('imageCropper.validate')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;